/**
 * Recolor cosmetic pixels while preserving outline-masked pixels.
 * Match-cursor mask: R encodes shade in [-1,1] as (shade+1)*127.5; A>0 = remap.
 */

function resolveImageSrc(src: string): string {
  const s = String(src || "").trim();
  if (!s) return s;
  // Lite catalog uses /api/cosmetic-asset URLs; also allow http(s) and data URLs.
  if (
    s.startsWith("data:") ||
    s.startsWith("http://") ||
    s.startsWith("https://") ||
    s.startsWith("/")
  ) {
    return s;
  }
  // Legacy raw base64 payloads.
  return `data:image/png;base64,${s}`;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image_load_failed"));
    img.src = resolveImageSrc(src);
  });
}

function hexToRgb(hex: string): [number, number, number] {
  const raw = String(hex || "").trim();
  const rgb = raw.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (rgb) {
    return [
      Math.max(0, Math.min(255, Math.round(Number(rgb[1])))),
      Math.max(0, Math.min(255, Math.round(Number(rgb[2])))),
      Math.max(0, Math.min(255, Math.round(Number(rgb[3])))),
    ];
  }
  const h = raw.replace("#", "");
  const n = parseInt(h.length === 3 ? h.replace(/./g, (c) => c + c) : h, 16);
  if (!Number.isFinite(n)) return [255, 255, 255];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  return `#${[clamp(r), clamp(g), clamp(b)].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

/** Encode shade [-1,1] as grayscale byte for match-cursor mask R channel. */
export function encodeMatchShadeByte(shade: number): number {
  const t = Math.max(-1, Math.min(1, Number(shade) || 0));
  return Math.round((t + 1) * 127.5);
}

export function decodeMatchShadeByte(byte: number): number {
  return byte / 127.5 - 1;
}

/**
 * Shade a hex color. `offset` in [-1, 1]: negative darkens, 0 keeps cursor color, positive lightens.
 */
export function shadeHexColor(hex: string, offset: number): string {
  const [r, g, b] = hexToRgb(hex);
  const t = Math.max(-1, Math.min(1, Number(offset) || 0));
  if (t < 0) {
    const k = 1 + t * 0.75; // -1 → 0.25, 0 → 1
    return rgbToHex(r * k, g * k, b * k);
  }
  if (t > 0) {
    return rgbToHex(r + (255 - r) * t, g + (255 - g) * t, b + (255 - b) * t);
  }
  return rgbToHex(r, g, b);
}

/** Clamp creator/API shade to [-1, 1]. */
export function parseCursorColorShade(raw: unknown): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.max(-1, Math.min(1, n));
}

async function loadImageData(
  src: string,
  w: number,
  h: number,
): Promise<Uint8ClampedArray | null> {
  try {
    const img = await loadImage(src);
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d")!;
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return ctx.getImageData(0, 0, w, h).data;
  } catch {
    return null;
  }
}

/**
 * Returns a PNG data URL with non-outline pixels tinted to `hexColor` (alpha preserved).
 * Outline pixels (mask alpha > 0) keep their original RGBA.
 */
export async function recolorCosmeticKeepingOutline(
  imageDataUrl: string,
  outlineMaskUrl: string | null | undefined,
  hexColor: string,
): Promise<string> {
  const base = await loadImage(imageDataUrl);
  const w = base.naturalWidth || base.width;
  const h = base.naturalHeight || base.height;
  if (w < 1 || h < 1) return imageDataUrl;

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(base, 0, 0, w, h);
  const img = ctx.getImageData(0, 0, w, h);

  const mask = outlineMaskUrl ? await loadImageData(outlineMaskUrl, w, h) : null;

  const [tr, tg, tb] = hexToRgb(hexColor);
  const data = img.data;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a === 0) continue;
    const isOutline = mask != null && mask[i + 3] > 0;
    if (isOutline) continue;
    data[i] = tr;
    data[i + 1] = tg;
    data[i + 2] = tb;
  }
  ctx.putImageData(img, 0, 0);
  return canvas.toDataURL("image/png");
}

/**
 * Remap only match-cursor mask pixels to shaded live cursor color (outline untouched).
 * Mask R = encodeMatchShadeByte(shade). If no mask but legacyFlat is true, remaps all
 * non-outline pixels (legacy items published before per-pixel masks).
 */
export async function applyMatchCursorTint(
  imageDataUrl: string,
  matchMaskUrl: string | null | undefined,
  cursorHex: string,
  opts?: {
    outlineMaskUrl?: string | null;
    /** When true and mask missing, remap all non-outline using flatShade. */
    legacyFlat?: boolean;
    legacyFlatShade?: number | null;
  },
): Promise<string> {
  const base = await loadImage(imageDataUrl);
  const w = base.naturalWidth || base.width;
  const h = base.naturalHeight || base.height;
  if (w < 1 || h < 1) return imageDataUrl;

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(base, 0, 0, w, h);
  const img = ctx.getImageData(0, 0, w, h);
  const data = img.data;

  const outline = opts?.outlineMaskUrl ? await loadImageData(opts.outlineMaskUrl, w, h) : null;
  const match = matchMaskUrl ? await loadImageData(matchMaskUrl, w, h) : null;

  if (match) {
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue;
      if (outline && outline[i + 3] > 0) continue;
      if (match[i + 3] === 0) continue;
      const [tr, tg, tb] = hexToRgb(shadeHexColor(cursorHex, decodeMatchShadeByte(match[i])));
      data[i] = tr;
      data[i + 1] = tg;
      data[i + 2] = tb;
    }
  } else if (opts?.legacyFlat) {
    const flat = shadeHexColor(cursorHex, opts.legacyFlatShade ?? 0);
    const [tr, tg, tb] = hexToRgb(flat);
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue;
      if (outline && outline[i + 3] > 0) continue;
      data[i] = tr;
      data[i + 1] = tg;
      data[i + 2] = tb;
    }
  } else {
    return imageDataUrl;
  }

  ctx.putImageData(img, 0, 0);
  return canvas.toDataURL("image/png");
}

/** Recolor each animation frame (no outline mask — outline lives on the static layer). */
export async function recolorAnimationFrames(
  frames: string[] | null | undefined,
  hexColor: string,
): Promise<string[] | null> {
  if (!frames?.length) return null;
  const out = await Promise.all(
    frames.slice(0, 10).map((f) => recolorCosmeticKeepingOutline(f, null, hexColor)),
  );
  return out;
}

export async function applyMatchCursorTintFrames(
  frames: string[] | null | undefined,
  matchMaskFrames: string[] | null | undefined,
  cursorHex: string,
  opts?: { legacyFlat?: boolean; legacyFlatShade?: number | null },
): Promise<string[] | null> {
  if (!frames?.length) return null;
  const out = await Promise.all(
    frames.slice(0, 10).map((f, i) => {
      const frameMask = matchMaskFrames?.[i] ?? null;
      return applyMatchCursorTint(f, frameMask, cursorHex, {
        legacyFlat: !frameMask && !!opts?.legacyFlat,
        legacyFlatShade: opts?.legacyFlatShade,
      });
    }),
  );
  return out;
}

export type MatchCursorCosmeticSource = {
  imageData: string;
  outlineMaskData?: string | null;
  animationFrames?: string[] | null;
  allowCustomColor?: boolean;
  matchCursorColor?: boolean;
  cursorColorShade?: number;
  matchCursorMaskData?: string | null;
  matchCursorMaskFrames?: string[] | null;
};

/** Apply purchase tint (optional) then live match-cursor remap for masked / legacy pixels. */
export async function resolveCosmeticVisual(
  item: MatchCursorCosmeticSource,
  opts: { cursorHex: string; customTint?: string | null },
): Promise<{ imageData: string; animationFrames: string[] | null }> {
  const frames =
    Array.isArray(item.animationFrames) && item.animationFrames.length
      ? item.animationFrames.filter((f) => typeof f === "string" && f.length > 0).slice(0, 10)
      : null;

  let imageData = item.imageData;
  let animationFrames = frames;

  const custom = opts.customTint;
  if (custom && item.allowCustomColor) {
    imageData = await recolorCosmeticKeepingOutline(imageData, item.outlineMaskData, custom);
    animationFrames = frames ? await recolorAnimationFrames(frames, custom) : null;
  }

  const hasMask = !!(item.matchCursorMaskData || item.matchCursorMaskFrames?.length);
  const hasMatch = hasMask || !!item.matchCursorColor;
  if (hasMatch) {
    imageData = await applyMatchCursorTint(imageData, item.matchCursorMaskData, opts.cursorHex, {
      outlineMaskUrl: item.outlineMaskData,
      legacyFlat: !item.matchCursorMaskData && !!item.matchCursorColor,
      legacyFlatShade: item.cursorColorShade ?? 0,
    });
    animationFrames = animationFrames
      ? await applyMatchCursorTintFrames(
          animationFrames,
          item.matchCursorMaskFrames,
          opts.cursorHex,
          {
            legacyFlat: !item.matchCursorMaskFrames?.length && !!item.matchCursorColor,
            legacyFlatShade: item.cursorColorShade ?? 0,
          },
        )
      : null;
  }

  return { imageData, animationFrames };
}
